import { OpenPanel } from '@openpanel/web';
import { IS_SA, OPENPANEL_API_URL, OPENPANEL_CLIENT_ID } from '../config/appConfig';
import { InvestorType } from '../constants/investorType';
import { Roles } from '../constants/roles';

// Analytics must never break a user flow: every export is fire-and-forget and
// a silent no-op when REACT_APP_OPENPANEL_CLIENT_ID is not configured.
let op = null;

// Backend kill-switch (SystemSettings key `openPanelEnabled`): nothing is sent
// unless the backend explicitly enables OpenPanel. The SDK is only initialized
// once the flag is confirmed true, so automatic screen views are gated too.
let runtimeEnabled = false;

// The kill-switch is THREE-state, not two. `runtimeEnabled === false` used to
// mean both "the backend said no" and "the backend has not answered yet" — and
// at boot it is always the latter. Events fired from component mount effects in
// that window were dropped outright, so on a first-ever visit (empty
// systemSettings cache) `signup_started` and `e_checker_visited` never reached
// OpenPanel at all. That under-counted precisely the first-time visitors the
// acquisition funnel exists to measure, and it was invisible because returning
// visitors — who have the cache — were unaffected.
// Until the flag resolves, calls are buffered; on resolve they are replayed
// (enabled) or discarded (disabled). Fail-closed is preserved: a session whose
// settings never resolve sends nothing.
let settingsResolved = false;
const PENDING_LIMIT = 50;
let pending = [];

// Every send funnels through here — including the replay — so the buffered and
// immediate paths cannot drift.
const dispatch = (send) => {
	if (!settingsResolved) {
		pending.push(send);
		if (pending.length > PENDING_LIMIT) {
			// Keep the newest — they sit closest to the funnel step actually reached.
			pending.shift();
		}
		return;
	}
	if (!runtimeEnabled || !op) {
		return;
	}
	try {
		send();
	} catch (error) {
		console.warn('OpenPanel send failed.', error);
	}
};

// Route transitions can remount a screen (e.g. declaration -> marketplace),
// which would double-fire its mount event. Dedupe applies ONLY to
// trackOpenPanelView (screen-view events); user-action events must never be
// deduped — two identical actions in quick succession are two real events.
const DEDUPE_WINDOW_MS = 2000;
let lastViewEventKey = null;
let lastViewEventAt = 0;

const getRegion = () => (IS_SA ? 'SA' : 'AE');
const getLanguage = () => (localStorage.getItem('i18nextLng') === 'ar' ? 'ar' : 'en');

// Docx user_type is investor | borrower; the JWT role for investors is 'Lender'.
const toUserType = (role) => {
	const roles = Array.isArray(role) ? role : [role];
	if (roles.includes(Roles.Investor)) {
		return 'investor';
	}
	if (roles.includes(Roles.Borrower)) {
		return 'borrower';
	}
	return null;
};

// Call whenever system settings are (re)loaded — from the localStorage cache
// at boot and from the fresh GetForFront response. Initializes the SDK the
// first time the flag is confirmed true; a flip to false stops all further
// sends in this session (auto screen views stop on the next load).
export const syncOpenPanelEnabled = (systemSettings) => {
	// A missing settings object means "not answered yet" — an empty localStorage
	// cache at boot, or a GetForFront that failed and resolved undefined through
	// its own .catch. It does NOT mean disabled, and resolving on it would drop
	// every event fired before the real answer lands.
	if (systemSettings == null) {
		return;
	}

	runtimeEnabled = systemSettings.openPanelEnabled === true;
	settingsResolved = true;

	if (runtimeEnabled) {
		initOpenPanel();
	}

	// The buffer is emptied either way: replayed when enabled and the SDK
	// constructed, dropped otherwise so a later enable cannot resurrect events
	// the backend already refused.
	const queued = pending;
	pending = [];
	if (runtimeEnabled && op) {
		queued.forEach((send) => dispatch(send));
	}
};

// GetForFront resolved without a usable body, or failed. Nothing else resolves
// the flag in this tab, so without this the session would buffer forever —
// holding every queued payload (and the PII it closes over) in memory until the
// tab closes. Resolve closed instead. A flag already resolved from the boot
// cache is left alone: a transient fetch failure must not disable a session the
// backend has confirmed enabled.
export const markOpenPanelSettingsUnavailable = () => {
	if (settingsResolved) {
		return;
	}
	settingsResolved = true;
	runtimeEnabled = false;
	pending = [];
};

export const initOpenPanel = () => {
	if (!runtimeEnabled || !OPENPANEL_CLIENT_ID || op) {
		return;
	}
	try {
		op = new OpenPanel({
			clientId: OPENPANEL_CLIENT_ID,
			...(OPENPANEL_API_URL ? { apiUrl: OPENPANEL_API_URL } : {}),
			// trackScreenViews is deliberately OFF. The SDK's own screenView() reads
			// window.location.href — query string included — and then stamps that
			// value onto every later track() as `__path`. Two of our routes carry
			// secrets there: /reset-password?userId=&code=<single-use reset token>
			// and /verify-email?e=<email>. With auto-tracking on, merely opening the
			// reset link from the email is enough to POST the token to the analytics
			// vendor. trackOpenPanelScreenView below passes an explicit
			// origin+pathname, so `__path` can never carry a query string.
			trackScreenViews: false,
			trackOutgoingLinks: true,
			trackAttributes: true,
		});
	} catch (error) {
		op = null;
		console.warn('OpenPanel init failed.', error);
	}
};

export const trackOpenPanel = (event, properties = {}) => {
	try {
		// Snapshot the payload at call time so a replayed event carries the context
		// it was fired in, not whatever the context is at flush time.
		const payload = { region: getRegion(), ...properties };
		dispatch(() => op.track(event, payload));
	} catch (error) {
		console.warn('OpenPanel track failed.', error);
	}
};

// Replaces the SDK's built-in screen-view tracking (see initOpenPanel). Sends
// origin + pathname only; the query string is dropped because it can hold a
// password-reset token or an email address. The SDK ignores a repeat of the
// path it last saw, so calling this on every render is safe.
export const trackOpenPanelScreenView = () => {
	try {
		const path = `${window.location.origin}${window.location.pathname}`;
		dispatch(() => op.screenView(path));
	} catch (error) {
		console.warn('OpenPanel screen view failed.', error);
	}
};

// For events fired from component-mount effects (screen views): identical
// event+props within the window count once, absorbing remount jitter.
//
// The kill-switch gate below runs before the dedupe bookkeeping so a view it
// suppresses cannot poison the window and swallow the first legitimate view
// after enabling. Unresolved settings are deliberately NOT a suppression: those
// views are buffered by trackOpenPanel, so they must still take part in dedupe
// or a remount inside the boot window would enqueue the same view twice.
export const trackOpenPanelView = (event, properties = {}) => {
	if (settingsResolved && (!runtimeEnabled || !op)) {
		return;
	}
	try {
		const eventKey = `${event}|${JSON.stringify(properties)}`;
		const now = Date.now();
		if (eventKey === lastViewEventKey && now - lastViewEventAt < DEDUPE_WINDOW_MS) {
			return;
		}
		lastViewEventKey = eventKey;
		lastViewEventAt = now;
	} catch (error) {
		console.warn('OpenPanel view-dedupe failed.', error);
	}
	trackOpenPanel(event, properties);
};

export const identifyOpenPanel = (user, traits = {}) => {
	if (!user?.nameid) {
		return;
	}
	try {
		const userType = toUserType(user.role);
		// account_status is deliberately NOT derived from the JWT status: the app's
		// enum (InComplete/Pending/Compliance/...) is not the docx vocabulary
		// (pending/verified/active/suspended), and a JWT-derived value would
		// overwrite the 'verified' set at declaration on the next token refresh.
		// It is set explicitly at funnel points (signup -> pending, declaration -> verified).
		const payload = {
			profileId: user.nameid,
			...(user.email ? { email: user.email } : {}),
			properties: {
				region: getRegion(),
				language: getLanguage(),
				currency: IS_SA ? 'SAR' : 'AED',
				user_id: user.nameid,
				...(user.email ? { email_address: user.email } : {}),
				...(userType ? { user_type: userType } : {}),
				...traits,
			},
		};
		dispatch(() => op.identify(payload));
	} catch (error) {
		console.warn('OpenPanel identify failed.', error);
	}
};

export const clearOpenPanel = () => {
	// Logout is an SPA navigate, not a reload, so this module's state survives the
	// user switch. Anything still buffered would replay after sign-out and re-bind
	// the SDK profile to the user who just left — and every event queued before the
	// next user identifies would be attributed to them. Drop it regardless of `op`:
	// inside the boot window `op` is always null and the early return below would
	// otherwise skip the reset entirely.
	pending = [];
	lastViewEventKey = null;
	lastViewEventAt = 0;
	if (!op) {
		return;
	}
	try {
		op.clear();
	} catch (error) {
		console.warn('OpenPanel clear failed.', error);
	}
};

// Docx §4.1 defines tier as retail | professional | corporate;
// the app's CorporateRetail folds into corporate (SPEC.md Decision #13).
export const toOpenPanelTier = (investorType) => {
	switch (investorType) {
		case InvestorType.Retail:
			return 'retail';
		case InvestorType.Professional:
			return 'professional';
		case InvestorType.Corporate:
		case InvestorType.CorporateRetail:
			return 'corporate';
		default:
			return null;
	}
};
